import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";

import GoogleIcon from "../../../assets/google-icon-logo.svg";
import Logo from "../../../assets/Logo.svg";

import styles from "../AuthWrapper.module.css";

import { loginUser } from "../services";
import { showToast } from "../../../utils/toast";
import { setUser, logoutUser } from "../../../redux/modules/userSlice";
import { setTokens, clearTokens } from "../../../utils/tokens";
import { REVOKED_ACCOUNT_MESSAGE } from "../../../utils/authErrors";
import {
  googleSignInAPI,
  verify2FAAPI,
  resend2FAAPI,
} from "../../../api/userApi";
import { useGoogleLogin } from "@react-oauth/google";
import { setRemember } from "../../../utils/refresh";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import AuthFooter from "../components/AuthFooter";
import { identifyMixpanelUser, setMixpanelPeopleProfile } from "../../../config/mixpanel";
import { isStagingCreatorOnboardingBypassEnabled } from "../../../config/stagingCreatorOnboarding";

type TwoFAData = {
  userId: string;
  cooldown?: number;
  remember?: boolean; // ⬅️ додали
};

function SignIn() {
  const seo = useSEO("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemembered] = useState(false); // ⬅️ додали стан remember
  const [showPassword, setShowPassword] = useState(false);
  const [twoFAData, setTwoFAData] = useState<TwoFAData | null>(null);
  const [code, setCode] = useState("");
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [hasAttemptedLogin, setHasAttemptedLogin] = useState(false);
  const [loginError, setLoginError] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const blockRevokedAccount = (user: { revoke?: boolean } | null | undefined) => {
    if (!user?.revoke) return false;
    clearTokens();
    dispatch(logoutUser());
    showToast(REVOKED_ACCOUNT_MESSAGE + ". Contact support.", "error");
    return true;
  };

  const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      signInHandler();
    }
  };

  const handle2FAKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleVerify2FA();
    }
  };

  useEffect(() => {
    if (!twoFAData) return;
    setSecondsLeft(twoFAData.cooldown ?? 60);
  }, [twoFAData]);

  useEffect(() => {
    if (!twoFAData || secondsLeft <= 0) return;
    const id = setInterval(
      () => setSecondsLeft(s => (s > 0 ? s - 1 : 0)),
      1000
    );
    return () => clearInterval(id);
  }, [twoFAData, secondsLeft]);

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse: { access_token: string }) => {
      try {
        // Get user info from Google using access token
        const userInfoResponse = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.access_token}`,
            },
          }
        );
        const userInfo = await userInfoResponse.json();

        // Send credential to backend
        googleSignInAPI({
          google_access_token: tokenResponse.access_token,
          email: userInfo.email,
          name: userInfo.name,
          picture: userInfo.picture,
        })
          .then(response => {
            if (blockRevokedAccount(response.data.user)) return;
            setTokens(response.data.tokens);
            dispatch(setUser(response.data.user));
            identifyMixpanelUser(response.data.user?.id);
            setMixpanelPeopleProfile({
              email: response.data.user?.email || userInfo?.email,
              name: response.data.user?.username || userInfo?.name,
            });
            // Зберігаємо інформацію про те, що користувач входив
            localStorage.setItem("hasRegisteredOrLoggedIn", "true");
            showToast("Login successful!");
            history.push("/");
          })
          .catch(err => {
            const errorMessage =
              err?.response?.data?.message || err?.message || "Login API error";
            showToast(errorMessage, "error");
          });
      } catch (error: any) {
        console.error("Google login error:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Error while signing in with Google!";
        showToast(errorMessage, "error");
      }
    },
    onError: () => {
      showToast("Error while signing in with Google!", "error");
    },
  });

  const signInHandler = () => {
    setHasAttemptedLogin(true);
    // ⬇️ передаємо remember в loginUser
    loginUser(email, password, remember)
      .then((res: any) => {
        setLoginError(false);
        if (res.twoFA) {
          // зберігаємо remember, щоб прокинути його на verify2FA
          setTwoFAData({
            userId: res.userId,
            cooldown: res.cooldown,
            remember: res.remember ?? remember, // ⬅️ важливо
          });
          return;
        }

        if (blockRevokedAccount(res?.user)) return;

        dispatch(setUser(res?.user));
        setTokens(res?.tokens);
        identifyMixpanelUser(res?.user?.id);
        setMixpanelPeopleProfile({
          email: res?.user?.email || email,
          name: res?.user?.username || null,
        });
        setRemember(remember);
        localStorage.setItem("hasRegisteredOrLoggedIn", "true");
        showToast("User logged in successfully!");
        history.push("/");
      })
      .catch(error => {
        setLoginError(true);
        // error може бути рядком (з services.js) або об'єктом помилки
        const errorMessage =
          typeof error === "string"
            ? error
            : error?.response?.data?.message ||
              error?.response?.message ||
              error?.message ||
              "Login failed";

        if (
          errorMessage === "Please verify your email!" ||
          (typeof error !== "string" &&
            error?.response?.data?.message
              ?.toLowerCase()
              .includes("verify your email"))
        ) {
          if (isStagingCreatorOnboardingBypassEnabled()) {
            showToast(
              "Staging: email verification is bypassed on the server. Retry login after signup.",
              "error"
            );
          } else {
            showToast("Please verify your email to continue", "error");
            setTimeout(() => {
              history.push("/email-activation");
            }, 2000);
          }
        } else {
          showToast(errorMessage, "error");
        }
      });
  };

  const handleForgotPassword = () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      showToast("Please enter your email address first.", "error");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      showToast("Please enter a valid email address.", "error");
      return;
    }
    history.push(`/forgot-password?email=${encodeURIComponent(trimmedEmail)}`);
  };

  const handleVerify2FA = async () => {
    if (!code || code.length !== 6) {
      return showToast("Enter valid 6-digit code", "error");
    }

    try {
      if (!twoFAData) return;
      const res = await verify2FAAPI({
        userId: twoFAData.userId,
        code,
        remember: twoFAData.remember ?? false, // ⬅️ прокидуємо на бек
      });

      if (blockRevokedAccount(res.user)) return;

      dispatch(setUser(res.user));
      setTokens(res.tokens);
      identifyMixpanelUser(res?.user?.id);
      setMixpanelPeopleProfile({
        email: res?.user?.email || email,
        name: res?.user?.username || null,
      });
      localStorage.setItem("hasRegisteredOrLoggedIn", "true");
      showToast("2FA verification successful!");
      history.push("/");
    } catch (err) {
      const error = err as any;
      showToast(error?.response?.data?.message || "Invalid 2FA code", "error");
    }
  };

  const handleResend = async () => {
    if (!twoFAData || secondsLeft > 0) return;
    try {
      const res = await resend2FAAPI({ userId: twoFAData.userId });
      setSecondsLeft(res?.cooldown ?? 60);
      showToast(res?.message || "Code resent to your email");
    } catch (err) {
      const error = err as any;
      const cooldown = error?.response?.data?.cooldown;
      if (typeof cooldown === "number") {
        setSecondsLeft(cooldown);
      }
      showToast(error?.response?.data?.message || "Resend failed", "error");
    }
  };

  // if(user) {
  //   return <Redirect to="/" />;
  // }

  if (twoFAData) {
    return (
      <>
        <StandardPageHelmet seo={seo} />
        <div className={styles.authWrapper}>
          <div className={styles.authLeft}>
            <div className={styles.authBox}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img src={Logo} alt="SugarCribs" className={styles.logo} />
              </div>
              <h2 className={styles.title}>Enter 2FA Code</h2>

              <input
                type="text"
                placeholder="6-digit code"
                maxLength={6}
                value={code}
                onChange={e =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                onKeyPress={handle2FAKeyPress}
                onPaste={e => {
                  const text =
                    (e.clipboardData || (window as any).clipboardData).getData(
                      "text"
                    ) || "";
                  const digits = (text.match(/\d/g) || []).join("").slice(0, 6);
                  if (digits.length) {
                    e.preventDefault();
                    setCode(digits);
                  }
                }}
                className={styles.inputField}
                autoComplete="one-time-code"
                name="one-time-code"
                id="two-factor-code"
                inputMode="numeric"
                pattern="\d{6}"
                enterKeyHint="done"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
              />

              <button className={styles.actionButton} onClick={handleVerify2FA}>
                Verify
              </button>

              <button
                className={styles.googleButton}
                onClick={handleResend}
                disabled={secondsLeft > 0}
                style={{ marginTop: 12, opacity: secondsLeft > 0 ? 0.7 : 1 }}
              >
                {secondsLeft > 0 ? `Resend (${secondsLeft}s)` : "Resend"}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <div className={styles.authWrapper}>
        <div className={styles.authLeft}>
          <div className={styles.authBox}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img src={Logo} alt="SugarCribs" className={styles.logo} />
            </div>
            <h2
              className={styles.title}
              style={{ textAlign: "center", marginTop: "48px" }}
            >
              Log in
            </h2>

            <label htmlFor="email">
              <span
                style={{
                  display: "block",
                  textAlign: "left",
                  marginBottom: 8,
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#ffffff",
                  fontFamily: "TT Norms, sans-serif",
                }}
              >
                E-mail
              </span>
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  if (hasAttemptedLogin) setLoginError(false);
                }}
                onKeyPress={handleKeyPress}
                className={`${styles.inputField} ${
                  hasAttemptedLogin && loginError ? styles.error : ""
                }`}
              />
            </label>
            <label
              htmlFor="password"
              style={{ position: "relative", marginTop: 10 }}
            >
              <span
                style={{
                  display: "block",
                  textAlign: "left",
                  marginBottom: 8,
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#ffffff",
                  fontFamily: "TT Norms, sans-serif",
                }}
              >
                Password
              </span>
              <div style={{ position: "relative" }}>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={e => {
                    setPassword(e.target.value);
                    if (hasAttemptedLogin) setLoginError(false);
                  }}
                  onKeyPress={handleKeyPress}
                  className={`${styles.inputField} ${
                    hasAttemptedLogin && loginError ? styles.error : ""
                  }`}
                  style={{ paddingRight: "50px" }}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#666",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {showPassword ? (
                    <Visibility style={{ fontSize: "24px" }} />
                  ) : (
                    <VisibilityOff style={{ fontSize: "24px" }} />
                  )}
                </button>
              </div>
            </label>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 10,
              }}
            >
              <button
                onClick={handleForgotPassword}
                className={styles.forgotPasswordButton}
              >
                Forgot password
              </button>
            </div>
            <div className={styles.rememberMe}>
              <div>
                {/* ⬇️ керований чекбокс */}
                <input
                  type="checkbox"
                  id="remember"
                  checked={remember}
                  onChange={e => setRemembered(e.target.checked)}
                />
                <label
                  htmlFor="remember"
                  style={{
                    marginTop: "8px",
                    fontSize: "14px",
                    fontWeight: 500,
                    fontFamily: "Inter, sans-serif",
                    color: "#ffffff",
                  }}
                >
                  Remember for 30 days
                </label>
              </div>
            </div>
            <button className={styles.actionButton} onClick={signInHandler}>
              Sign in
            </button>

            <button
              className={styles.googleButton}
              onClick={() => googleLogin()}
            >
              <img src={GoogleIcon} alt="Google" /> Sign in with Google
            </button>
            <p className={styles.switchMode}>
              Don't have an account?
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => history.push("/register")}
                style={{
                  fontWeight: 600,
                  color: "#FE5A97",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "17px",
                  textDecoration: "underline",
                }}
              >
                Sign up
              </button>
            </p>
            <p
              style={{
                marginTop: 12,
                marginBottom: 8,
                fontSize: "12px",
                lineHeight: 1.4,
                color: "#C7CBD1",
                textAlign: "center",
              }}
            >
              By signing up, you agree to our{" "}
              <a
                href="/terms-of-service"
                style={{ color: "#FE5A97", textDecoration: "underline" }}
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="/privacy-policy"
                style={{ color: "#FE5A97", textDecoration: "underline" }}
              >
                Privacy Policy
              </a>
              , and confirm that you are at least 18 years of age.
            </p>
          </div>
        </div>
        <AuthFooter />
      </div>
    </>
  );
}

export default SignIn;
